import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Tracks whether the user has scrolled past a "hero" element. The hook
 * returns true the moment the hero's bottom edge passes the sticky
 * header's bottom — useful for triggering a context bar that slides in
 * once the page's primary identity (title/date/code) leaves the viewport.
 *
 * Usage:
 *   const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();
 *   <section ref={heroRef}>…</section>
 *   <ContextBar visible={scrolledPast} … />
 */
export function useScrollPastHero<T extends HTMLElement>(): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [past, setPast] = useState(false);
  // The Header measures itself into --header-h, so re-read it on resize:
  // rootMargin is fixed when the observer is constructed, and a stale one
  // would trip the bar in at the wrong scroll position after a height change.
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const read = () =>
      setHeaderH(
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue("--header-h"),
          10,
        ) || 108,
      );
    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || !headerH) return;
    const obs = new IntersectionObserver(
      ([entry]) => setPast(!entry.isIntersecting),
      { rootMargin: `-${headerH}px 0px 0px 0px`, threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [headerH]);
  return [ref, past];
}
